import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  effect,
  signal,
} from '@angular/core'
import { Feature, FeatureCollection } from 'geojson'
import { ToastService } from 'src/app/shared/services/toast.service'
import { SimulationWarningsEditorDataService } from '../../services/simulation-warnings-editor-data.service'
import { SimulationWarningsLegacyImportService } from '../../services/simulation-warnings-legacy-import.service'
import { CustomerMetricMap } from '../../enums/simulation-metrics.enum'
import { MetricModel } from 'src/app/shared/models/metric.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AuthService } from 'src/app/core/services/auth.service'
import { AppPaths } from 'src/app/app-routing.module'
import { MapService } from 'src/app/core/services/map.service'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import {
  isAreaSimWarningDataModel,
  isSegmentSimWarningDataModel,
  SimWarningDataModel,
} from 'src/app/shared/models/sim-warning-creation.model'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { Permissions } from 'src/app/core/enums/permissions.enum'

@Component({
  selector: 'app-simulation-warnings-editor',
  templateUrl: './simulation-warnings-editor.component.html',
  styleUrl: './simulation-warnings-editor.component.scss',
  standalone: false,
})
export class SimulationWarningsEditorComponent {
  @Input() isVisible: boolean = false
  @Output() closed = new EventEmitter<void>()
  @Output() helpRequested = new EventEmitter<void>()
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>
  @ViewChildren('warningListItem') warningListItems!: QueryList<ElementRef<HTMLButtonElement>>

  public isLoading: boolean = false
  public readonly isPushLoading = signal(false)
  public readonly legacyImportCompatibilityEnabled =
    this.legacyImportService.legacyImportCompatibilityEnabled
  public showLegacyImportForm = false
  public legacyImportText = ''
  public legacyImportFailures: {
    id: string
    type: 'segment' | 'area' | 'unknown'
    reason: string
  }[] = []
  public readonly selectedWarning = signal<Feature<
    GeoJSON.LineString | GeoJSON.Polygon,
    SimWarningDataModel
  > | null>(null)
  public selectedWarningTtlSeconds: number = 0
  public selectedWarningTargets: string[] = []
  public selectedWarningConfidence: number = 1.0
  public selectedWarningDfrsClassification: 'DFRS_NONE' | 'DFRS_ONLY' | 'DFRS_PARTLY' = 'DFRS_NONE'
  public readonly Permissions = Permissions

  constructor(
    public editorService: SimulationWarningsEditorDataService,
    private readonly legacyImportService: SimulationWarningsLegacyImportService,
    private readonly toastService: ToastService,
    private readonly appState: AppStateService,
    private readonly authService: AuthService,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private readonly simulationWarningLayerService: LayerSimulationWarningService,
  ) {
    effect(() => {
      const clickedFeature = this.mapEventsService.clickedFeatures()[0]
      if (
        clickedFeature &&
        (clickedFeature.layer?.id === `${Layers.SimEditorLayer.name}-symbols` ||
          clickedFeature.layer?.id === `${Layers.SimEditorLayer.name}-corrupted-symbols` ||
          clickedFeature.layer?.id === `${Layers.SimEditorLayer.name}-hexagons` ||
          clickedFeature.layer?.id === `${Layers.SimEditorLayer.name}-lines`)
      ) {
        const featureId = clickedFeature?.properties?.['featureId'] ?? clickedFeature.id?.toString()
        if (!featureId) return

        const warning = [
          ...this.editorService.getValidWarnings(),
          ...this.editorService.getCorruptedWarnings(),
        ].find(
          (feature) =>
            feature.properties.featureId === featureId || feature.id?.toString() === featureId,
        )

        if (warning) {
          this.selectWarning(warning)
        }
      }
    })
  }

  isWarningSelected(featureId: string | undefined): boolean {
    return !!featureId && this.appState.getSelectedFeatureId() === featureId
  }

  getAvailableScopes(): string[] {
    return this.authService.getUserClaims().userBackendScopes
  }

  isTargetSelected(target: string): boolean {
    return this.selectedWarningTargets.includes(target)
  }

  isSelectedWarningInvalid(): boolean {
    return this.selectedWarning()?.properties?.valid === false
  }

  toggleTargetSelection(target: string): void {
    const index = this.selectedWarningTargets.indexOf(target)
    if (index > -1) {
      this.selectedWarningTargets.splice(index, 1)
    } else {
      this.selectedWarningTargets.push(target)
    }
  }

  getWarningIdentifier(
    w: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): string {
    let warningId: string = ''
    if (isAreaSimWarningDataModel(w.properties)) {
      warningId = w.properties.h3id
    } else if (isSegmentSimWarningDataModel(w.properties)) {
      warningId = w.properties.segmentId
    }
    return warningId ?? ''
  }

  importFromFile(): void {
    this.fileInput.nativeElement.click()
  }

  // TODO: delete this legacy import UI in the future once the deprecated payload format is removed.
  openLegacyImportForm(): void {
    this.showLegacyImportForm = true
  }

  // TODO: delete this legacy import form close logic in the future when legacy import support is removed.
  closeLegacyImportForm(): void {
    this.showLegacyImportForm = false
    this.legacyImportText = ''
    this.legacyImportFailures = []
  }

  // TODO: delete this legacy import handling in the future after all old payloads are migrated.
  async saveLegacyImport(): Promise<void> {
    if (!this.legacyImportText.trim()) {
      this.toastService.show({
        type: 'warning',
        message: 'Paste a legacy warning payload before saving',
      })
      return
    }

    try {
      const rawPayload = JSON.parse(this.legacyImportText)

      if (!rawPayload || (typeof rawPayload !== 'object' && !Array.isArray(rawPayload))) {
        this.toastService.show({
          type: 'warning',
          message: 'Legacy import must be a JSON object or array.',
        })
        return
      }

      const legacyImportResult = await this.legacyImportService.importLegacyWarnings(rawPayload)
      const featureCollection = legacyImportResult.featureCollection
      const failedList = legacyImportResult.failedWarnings
        .slice(0, 10)
        .map((warning) =>
          warning.type === 'segment'
            ? `segmentId=${warning.id}`
            : warning.type === 'area'
              ? `h3id=${warning.id}`
              : warning.id,
        )

      if (legacyImportResult.failedWarnings.length) {
        this.legacyImportFailures = legacyImportResult.failedWarnings.map((warning) => ({
          id: warning.id,
          type: warning.type,
          reason:
            warning.type === 'area'
              ? 'H3 element is not valid'
              : warning.type === 'segment'
                ? 'Segment geometry could not be resolved'
                : 'Element is not valid',
        }))
        return
      }

      this.legacyImportFailures = []

      if (!featureCollection.features.length) {
        this.toastService.show({
          type: 'warning',
          message: 'No valid legacy warnings were found in the pasted JSON.',
        })
        return
      }

      this.editorService.clearWarnings()
      await this.editorService.importFromFeatureCollection(featureCollection)
      this.closeLegacyImportForm()
      this.toastService.show({
        type: 'success',
        message: 'Legacy warnings imported and normalized',
      })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown JSON parsing error'
      this.toastService.show({
        type: 'warning',
        message: `Legacy JSON is invalid: ${message}`,
      })
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const featureCollection = JSON.parse(e.target?.result as string) as FeatureCollection<
          GeoJSON.LineString | GeoJSON.Polygon,
          SimWarningDataModel
        >

        // Clear existing warnings before importing
        this.editorService.clearWarnings()

        await this.editorService.importFromFeatureCollection(featureCollection)
        this.toastService.show({ type: 'success', message: 'File imported successfully' })
      } catch (error: any) {
        const statusCode = error?.status || 'Unknown'
        this.toastService.show({
          type: 'error',
          message: `Failed to validate file (${statusCode})`,
        })
      }
      input.value = ''
    }
    reader.readAsText(input.files[0])
  }

  exportToFile(): void {
    const featureCollection = this.editorService.exportToFeatureCollection()
    const json = JSON.stringify(featureCollection, undefined, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'simulation-warnings.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  async pushWarnings(event: MouseEvent): Promise<void> {
    event.stopPropagation()
    if (!this.editorService.getValidWarnings().length) {
      this.toastService.show({ type: 'error', message: 'No warnings available to push' })
      return
    }
    if (this.editorService.getCorruptedWarnings().length) {
      this.toastService.show({
        type: 'error',
        message: 'Resolve corrupted warnings before pushing',
      })
      return
    }
    this.isPushLoading.set(true)
    try {
      const { succeeded, failed } = await this.editorService.pushAllToSimulation()
      if (failed === 0) {
        this.toastService.show({
          type: 'success',
          message: `${succeeded} warning(s) pushed to simulation`,
        })
        this.simulationWarningLayerService.startReloadingAfterMutation()
        this.close()
      } else if (succeeded === 0) {
        this.toastService.show({
          type: 'error',
          message: `Failed to push all ${failed} warning(s) to simulation`,
        })
      } else {
        this.toastService.show({
          type: 'warning',
          message: `${succeeded} warning(s) pushed, ${failed} failed`,
        })
        this.simulationWarningLayerService.startReloadingAfterMutation()
        this.close()
      }
    } catch {
      this.toastService.show({ type: 'error', message: 'Failed to push warnings to simulation' })
    } finally {
      this.isPushLoading.set(false)
    }
  }

  getMetricForWarning(
    w: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): MetricModel | undefined {
    return [...CustomerMetricMap.values()].find((m) => m.name === w.properties.warningName)
  }

  getWarningImagePath(warningName: string): string {
    // because 'accident' is falling out of the naming convention, we need to handle it separately
    // for other warnings, we can use PascalCase with a 'Test' prefix to get the correct image name
    const testWarningName =
      warningName === 'accident'
        ? 'TestVehicleAccident'
        : `Test${warningName.charAt(0).toUpperCase()}${warningName.slice(1)}`
    return this.mapService.getImageUriById(testWarningName)
  }

  selectWarning(w: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>): void {
    this.selectedWarning.set({ ...w })
    this.selectedWarningTtlSeconds = this.convertTtlMsToSeconds(w.properties.ttl)
    this.selectedWarningTargets = w.properties.targets?.slice() ?? []
    this.selectedWarningConfidence = w.properties.confidence ?? 1.0
    this.selectedWarningDfrsClassification = w.properties.dfrsClassification ?? 'DFRS_NONE'
    this.scrollSelectedWarningIntoView(w.properties.featureId)
    this.appState.setState(AppPaths.Simulation, { selectedFeatureId: w.properties.featureId })

    const coordinates = this.getWarningCoordinates(w)
    if (!coordinates) {
      this.toastService.show({ type: 'warning', message: 'Warning coordinates are unavailable' })
      return
    }

    const [lng, lat] = coordinates
    this.mapService.map.flyTo({
      center: [lng, lat],
      zoom: Math.max(this.mapService.map.getZoom(), 13),
      essential: true,
      duration: 900,
    })
  }

  private scrollSelectedWarningIntoView(featureId: string | undefined): void {
    if (!featureId) return

    queueMicrotask(() => {
      const item = this.warningListItems.find(
        (element) => element.nativeElement.dataset['featureId'] === featureId,
      )

      item?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    })
  }

  private getWarningCoordinates(
    w: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): [number, number] | null {
    const properties = w?.properties ?? {}

    if (Number.isFinite(properties.longitude) && Number.isFinite(properties.latitude)) {
      return [Number(properties.longitude), Number(properties.latitude)]
    }
    return null
  }

  saveSelectedWarning(): void {
    const selectedWarning = this.selectedWarning()
    if (!selectedWarning) return
    const ttlMs = this.convertTtlSecondsToMs(this.selectedWarningTtlSeconds)
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
      this.toastService.show({ type: 'error', message: 'TTL must be greater than 0 seconds' })
      return
    }

    const hasExtendedAccess = this.authService.hasPermission([
      Permissions.RCS_Apps_Simulation_Extended_Access,
    ])

    this.editorService.updateWarning({
      ...selectedWarning,
      properties: {
        ...selectedWarning.properties,
        ttl: ttlMs,
        targets: this.selectedWarningTargets.length > 0 ? this.selectedWarningTargets : null,
        ...(hasExtendedAccess
          ? {
              confidence: this.selectedWarningConfidence,
              dfrsClassification: this.selectedWarningDfrsClassification,
            }
          : {
              confidence: selectedWarning.properties.confidence,
              dfrsClassification: selectedWarning.properties.dfrsClassification ?? 'DFRS_NONE',
            }),
      },
    })
    this.toastService.show({ type: 'success', message: 'Warning updated', delay: 1500 })
  }

  removeSelectedWarning(): void {
    const selectedWarning = this.selectedWarning()
    if (!selectedWarning?.properties.featureId) return
    this.editorService.removeWarning(selectedWarning.properties.featureId)
    this.selectedWarning.set(null)
    this.selectedWarningTtlSeconds = 0
    this.selectedWarningTargets = []
    this.selectedWarningConfidence = 1.0
    this.selectedWarningDfrsClassification = 'DFRS_NONE'
    this.toastService.show({ type: 'success', message: 'Warning removed' })
    this.appState.setState(AppPaths.Simulation, { selectedFeatureId: undefined })
  }

  deselectWarning(): void {
    this.selectedWarning.set(null)
    this.selectedWarningTtlSeconds = 0
    this.selectedWarningTargets = []
    this.selectedWarningConfidence = 1.0
    this.selectedWarningDfrsClassification = 'DFRS_NONE'
    this.appState.setState(AppPaths.Simulation, { selectedFeatureId: undefined })
  }

  private convertTtlMsToSeconds(ms: number): number {
    return Math.round(ms / 1000)
  }

  private convertTtlSecondsToMs(seconds: number): number {
    return seconds * 1000
  }

  close(): void {
    this.closed.emit()
  }
}
